"use client";

import React, { forwardRef, useId } from "react";
import { cn } from "utils/utils";

/**
 * Enhanced Input Component with DaisyUI v5 Support
 *
 * @example Basic vertical input (fieldset pattern)
 * ```tsx
 * <Input label="Name" placeholder="Enter your name" />
 * ```
 *
 * @example Horizontal with icons and keyboard shortcuts
 * ```tsx
 * <Input
 *   layout="horizontal"
 *   placeholder="Search"
 *   startIcon={<SearchIcon className="h-4 w-4" />}
 *   kbdShortcuts={["⌘", "K"]}
 * />
 * ```
 *
 * @example Horizontal with complex content (badges, text)
 * ```tsx
 * <Input
 *   layout="horizontal"
 *   placeholder="Enter file path"
 *   startContent={<FileIcon className="h-4 w-4" />}
 *   endContent={<span className="badge badge-neutral badge-xs">Optional</span>}
 * />
 * ```
 *
 * @example Horizontal with inline label
 * ```tsx
 * <Input
 *   layout="horizontal"
 *   inlineLabel="Path"
 *   placeholder="src/app/"
 *   endContent={<span className="badge badge-neutral badge-xs">Optional</span>}
 * />
 * ```
 *
 * @example Downshift library compatibility
 * ```tsx
 * <Input
 *   {...getInputProps()}
 *   layout="horizontal"
 *   startIcon={<SearchIcon />}
 *   placeholder="Type to search..."
 * />
 * ```
 *
 * @example Error states with colors
 * ```tsx
 * <Input
 *   label="Password"
 *   type="password"
 *   error="Password is required"
 *   color="error"
 * />
 * ```
 */

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "color"> {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  isLoading?: boolean;
  // DaisyUI-specific props to match Select component
  size?: "xs" | "sm" | "md" | "lg";
  variant?: "ghost";
  color?:
    | "primary"
    | "secondary"
    | "accent"
    | "info"
    | "success"
    | "warning"
    | "error";
  // Layout options - DaisyUI v5 label positions
  layout?: "vertical" | "horizontal";
  labelWidth?: string; // Custom width for horizontal layout
  // Icon support (backward compatibility)
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  // DaisyUI v5 enhanced content support
  startContent?: React.ReactNode; // For badges, text prefixes, complex content
  endContent?: React.ReactNode; // For badges, text suffixes, complex content
  kbdShortcuts?: string[]; // For keyboard shortcuts like ["⌘", "K"]
  // Inline label support for wrapper layout
  inlineLabel?: string; // Text label inside the wrapper (e.g., "Path")
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      description,
      error,
      required = false,
      isLoading = false,
      size = "md",
      variant,
      color,
      layout = "vertical",
      labelWidth = "w-fit",
      startIcon,
      endIcon,
      startContent,
      endContent,
      kbdShortcuts,
      inlineLabel,
      className,
      disabled,
      placeholder,
      "aria-labelledby": ariaLabelledByProp,
      id: idProp,
      ...props
    },
    ref
  ) => {
    // IDs for ARIA relationships
    // Use externally-provided id (e.g. from Downshift's getInputProps()) if available,
    // so that <label htmlFor> always matches the <input id>.
    const generatedId = useId();
    const inputId = idProp || generatedId;
    const labelId = useId();
    const descriptionId = useId();
    const errorId = useId();

    // Build ARIA describedby
    const ariaDescribedBy =
      [description ? descriptionId : null, error ? errorId : null]
        .filter(Boolean)
        .join(" ") || undefined;

    // Build input classes
    const inputClasses = cn(
      "input",
      {
        // Variant (in v5, inputs have border by default, use ghost to remove)
        "input-ghost focus-within:outline-transparent focus:outline-transparent":
          variant === "ghost",
        // Size
        "input-xs": size === "xs",
        "input-sm": size === "sm",
        "input-lg": size === "lg",
        // Color
        "input-primary": color === "primary",
        "input-secondary": color === "secondary",
        "input-accent": color === "accent",
        "input-info": color === "info",
        "input-success": color === "success",
        "input-warning": color === "warning",
        "input-error": color === "error" || error,
        "focus:outline-base-content/50 focus-within:outline-base-content/50":
          !color && !error,
        "focus:border-base-content/50 focus-within:border-base-content/50":
          !color && !error,
      },
      // Placeholder styling
      "placeholder:text-base-content/50",

      // Custom classes
      className
    );

    // Determine content for wrapper layout
    const finalStartContent = startContent || startIcon;
    const finalEndContent = endContent || endIcon;

    // Conditional rendering based on layout

    if (layout === "horizontal") {
      // DaisyUI v5 wrapper pattern: <label className="input">content</label>

      return (
        <div className={cn("w-full", className)}>
          {/* External Label (outside wrapper for better accessibility) */}
          {label && (
            <label
              className="text-base-content mb-1 block text-sm font-medium"
              htmlFor={inputId}
              id={labelId}
            >
              {label}
              {required && <span className="text-error ml-1">*</span>}
            </label>
          )}

          {/* Description */}
          {description && (
            <div id={descriptionId} className="mb-1">
              <span className="text-base-content/70 text-sm">
                {description}
              </span>
            </div>
          )}

          {/* DaisyUI v5 wrapper pattern — explicit htmlFor ensures a11y tools
              recognise the label/control association even with DaisyUI wrapper classes */}
          <label
            htmlFor={inputId}
            className={cn(
              "input flex w-full items-center gap-2",
              // "focus:outline-base-content/50 focus-within:outline-base-content/50",
              // "focus:border-base-content/50 focus-within:border-base-content/50",
              {
                "input-ghost focus-within:outline-transparent focus:outline-transparent":
                  variant === "ghost",
                "input-xs": size === "xs",
                "input-sm": size === "sm",
                "input-lg": size === "lg",
                "input-primary": color === "primary",
                "input-secondary": color === "secondary",
                "input-accent": color === "accent",
                "input-info": color === "info",
                "input-success": color === "success",
                "input-warning": color === "warning",
                "input-error": color === "error" || error,

                "focus:outline-base-content/50 focus-within:outline-base-content/50":
                  !variant,
                "focus:border-base-content/50 focus-within:border-base-content/50":
                  !variant,
              }
            )}
          >
            {/* Visually hidden label text to prevent empty-label a11y violation.
                id is set to the downshift-provided aria-labelledby target (if any)
                so that the reference resolves correctly. */}
            <span
              id={ariaLabelledByProp ?? (label ? labelId : undefined)}
              className="sr-only"
            >
              {label || placeholder}
            </span>

            {/* Start Icon with proper opacity */}
            {finalStartContent && (
              <div className="flex items-center opacity-50">
                {finalStartContent}
              </div>
            )}

            {/* Inline Label (inside wrapper) */}
            {inlineLabel && (
              <span className="shrink-0 text-current opacity-75">
                {inlineLabel}
              </span>
            )}

            <input
              ref={ref}
              id={inputId}
              disabled={disabled}
              required={required}
              placeholder={placeholder}
              className={cn("placeholder:text-current/50 grow bg-transparent")}
              aria-labelledby={
                ariaLabelledByProp ?? (label ? labelId : undefined)
              }
              aria-describedby={ariaDescribedBy}
              aria-invalid={error ? "true" : undefined}
              {...props}
            />

            {/* Loading Spinner */}
            {isLoading && (
              <span className="loading loading-spinner loading-xs opacity-60"></span>
            )}

            {/* End Content */}
            {!isLoading && finalEndContent && (
              <div className="flex items-center">{finalEndContent}</div>
            )}

            {/* Keyboard Shortcuts */}
            {!isLoading && kbdShortcuts && kbdShortcuts.length > 0 && (
              <div className="flex items-center gap-1">
                {kbdShortcuts.map((shortcut, index) => (
                  <kbd key={index} className="kbd kbd-sm">
                    {shortcut}
                  </kbd>
                ))}
              </div>
            )}
          </label>

          {/* Error Message */}
          {error && (
            <div id={errorId} className="mt-1">
              <span className="text-error text-sm">{error}</span>
            </div>
          )}
        </div>
      );
    }

    // Default vertical layout (now using fieldset pattern)
    return (
      <fieldset className={cn("fieldset", className)}>
        {/* Legend (label) */}
        {label && (
          <legend className="fieldset-legend" id={labelId}>
            {label}
            {required && <span className="text-error ml-1">*</span>}
          </legend>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Start Icon */}
          {startIcon && (
            <div className="text-base-content/60 pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2">
              {startIcon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            required={required}
            placeholder={placeholder}
            className={inputClasses}
            aria-labelledby={label ? labelId : undefined}
            aria-label={!label ? placeholder : undefined}
            aria-describedby={ariaDescribedBy}
            aria-invalid={error ? "true" : undefined}
            {...props}
          />

          {/* End Icon or Loading */}
          {(endIcon || isLoading) && (
            <div className="absolute right-3 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center">
              {isLoading ? (
                <span className="loading loading-spinner loading-xs text-base-content/60 pointer-events-none"></span>
              ) : (
                <div className="text-base-content/60 flex items-center justify-center">
                  {endIcon}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Description as label (DaisyUI fieldset pattern) */}
        {description && <p className="label">{description}</p>}

        {/* Error Message */}
        {error && (
          <div id={errorId} className="mt-1">
            <span className="text-error text-sm">{error}</span>
          </div>
        )}
      </fieldset>
    );
  }
);

Input.displayName = "Input";
